/**
 * Plain-language payment / cash-in event labels for Stage Finance ledgers.
 * Labels describe real cash events — not scorecard metrics.
 */

import { n } from './math.js';
import { normalizeConstructionPaymentTranches } from './paymentTiming.js';

/** Month (1-indexed) for each construction tranche under the current schedule. */
export function constructionTrancheMonths(programme, tranches) {
  const shares = normalizeConstructionPaymentTranches(tranches);
  const start = Math.max(1, programme.constructionStart || 1);
  const end = Math.max(start, programme.constructionEnd || start);
  const span = end - start + 1;
  const count = shares.length;
  const months = [];
  for (let i = 0; i < count; i += 1) {
    const offset = count === 1 || span === 1
      ? 0
      : Math.round((i * (span - 1)) / (count - 1));
    months.push(Math.min(end, start + offset));
  }
  return months;
}

/**
 * Short payment-due note from cost atoms paid this month.
 * Plain labels: Land deposit, Land closing, Feasibility,
 * Construction tranche 1–5, Tax, Delivery.
 */
export function paymentDueNote(costDetail = {}, ctx = {}) {
  const {
    month,
    programme = {},
    assumptions = {},
    isDelivery = false,
  } = ctx;
  const d = costDetail || {};
  const labels = [];

  const landPaid = n(d.landPrice) + n(d.acquisitionCosts);
  if (landPaid > 1e-9) {
    const signingMonth = assumptions.landSigningMonth || 1;
    const depositPct = assumptions.landDepositPct || 0;
    const closingMonth = programme.acquisitionMonth;
    if (depositPct > 0 && month === signingMonth && month !== closingMonth) {
      labels.push('Land deposit');
    } else if (month === closingMonth) {
      labels.push(depositPct > 0 && month === signingMonth ? 'Land deposit + closing' : 'Land closing');
    } else {
      labels.push('Land');
    }
  }

  if (n(d.feasibilityCosts) > 1e-9) {
    labels.push('Feasibility');
  }

  const buildPaid = n(d.constructionCost) + n(d.constructionReserve);
  if (buildPaid > 1e-9) {
    const trancheMonths = constructionTrancheMonths(
      programme,
      assumptions.constructionPaymentTranches,
    );
    const hits = [];
    trancheMonths.forEach((m, idx) => {
      if (m === month) hits.push(idx + 1);
    });
    if (hits.length === 1) labels.push(`Construction tranche ${hits[0]}`);
    else if (hits.length > 1) labels.push(`Construction tranches ${hits.join('+')}`);
    else labels.push('Construction');
  }

  if (n(d.professionalSoftCosts) > 1e-9) labels.push('Soft costs');
  if (n(d.commercialCosts) > 1e-9) labels.push('Commercial');
  if (n(d.financingCosts) > 1e-9) {
    // Interest is the only acceptable monthly recurring financing cost.
    labels.push(isDelivery ? 'Interest / financing' : 'Interest (monthly)');
  }
  if (n(d.taxes) > 1e-9) labels.push('Tax');
  if (isDelivery) labels.push('Delivery');

  return labels.join(', ');
}

/** Atomic cash-in source labels present on a month or stage totals row. */
export function cashInSourceLabels(row = {}) {
  const parts = [];
  if (n(row.spvContribution) > 1e-9) parts.push('SPV capital');
  if (n(row.landFacilityDraw) > 1e-9) parts.push('Land facility');
  if (n(row.constructionFacilityDraw) > 1e-9) parts.push('Construction facility');
  if (n(row.securedPartnerDraw) > 1e-9) parts.push('Partner');
  if (n(row.usableBuyerCash) > 1e-9) parts.push('Buyer cash');
  return parts;
}

/** Short cash-in source note for a single month (monthly detail). */
export function cashInNote(row = {}) {
  return cashInSourceLabels(row).join(', ');
}

/**
 * Stage-statement cash-in note: each atomic source once, joined with +.
 * Example: "SPV capital + Land facility + Buyer cash"
 * — never "SPV capital; SPV capital, Land facility".
 */
export function stageCashInNote(row = {}) {
  return cashInSourceLabels(row).join(' + ');
}

/**
 * Stage-statement payments-due note: short stage summary, not every monthly event.
 * preparation → Land closing + Feasibility
 * construction → Construction tranches + soft/commercial/financing
 * delivery → tax + delivery
 * Month-by-month event history stays on monthly paymentDueNote only.
 */
export function stagePaymentsDueNote(stage, costDetail = {}) {
  const d = costDetail || {};
  const landPaid = n(d.landPrice) + n(d.acquisitionCosts);
  const feasibility = n(d.feasibilityCosts);
  const build = n(d.constructionCost) + n(d.constructionReserve);
  const soft = n(d.professionalSoftCosts);
  const commercial = n(d.commercialCosts);
  const financing = n(d.financingCosts);
  const taxes = n(d.taxes);
  const labels = [];

  if (stage === 'preparation') {
    if (landPaid > 1e-9) labels.push('Land closing');
    if (feasibility > 1e-9) labels.push('Feasibility');
  } else if (stage === 'authorization') {
    if (feasibility > 1e-9) labels.push('Feasibility');
    if (soft > 1e-9) labels.push('Soft costs');
    if (commercial > 1e-9) labels.push('Commercial');
    if (financing > 1e-9) labels.push('Interest');
  } else if (stage === 'construction') {
    if (build > 1e-9) labels.push('Construction tranches');
    if (soft > 1e-9) labels.push('Soft costs');
    if (commercial > 1e-9) labels.push('Commercial');
    if (financing > 1e-9) labels.push('Interest');
  } else if (stage === 'delivery') {
    if (taxes > 1e-9) labels.push('Tax');
    if (financing > 1e-9) labels.push('Interest');
    const anyCost = landPaid + feasibility + build + soft + commercial + financing + taxes > 1e-9;
    if (anyCost) labels.push('Delivery');
  }

  return labels.join(' + ');
}

/** Aggregate unique non-empty note strings (legacy; prefer stage* helpers for stage rows). */
export function joinNotes(notes) {
  const seen = new Set();
  const out = [];
  for (const note of notes || []) {
    const cleaned = String(note || '').trim();
    if (!cleaned || seen.has(cleaned)) continue;
    seen.add(cleaned);
    out.push(cleaned);
  }
  return out.join('; ');
}
